import React, { useState, useEffect } from 'react';
import { Offcanvas, Button } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import Swal from 'sweetalert2';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3333';

const EntryEditModal = ({ show, onHide, transaction, ledgers, onSuccess }) => {
    const [date, setDate] = useState('');
    const [narration, setNarration] = useState('');
    const [items, setItems] = useState([]);
    const [entrytypeId, setEntrytypeId] = useState('');
    const [entrytypes, setEntrytypes] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchEntrytypes = async () => {
            try {
                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const token = userInfo?.token;
                const response = await axios.get(`${API_BASE_URL}/api/entrytypes`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.data && response.data.status === 'success') {
                    setEntrytypes(response.data.data || []);
                } else if (Array.isArray(response.data)) {
                    setEntrytypes(response.data);
                }
            } catch (error) {
                console.error("Error fetching entrytypes:", error);
            }
        };
        fetchEntrytypes();
    }, []);

    useEffect(() => {
        if (transaction) {
            setDate(transaction.date ? new Date(transaction.date).toISOString().split('T')[0] : '');
            setNarration(transaction.narration || '');
            setEntrytypeId(transaction.entrytype_id || '');
            
            // Format existing items for the edit form
            const formattedItems = transaction.items?.map(item => ({
                id: item.Entryitem?.id,
                dc: item.Entryitem?.dc,
                ledger_id: item.Entryitem?.ledger_id,
                amount: item.Entryitem?.amount
            })) || [];
            
            setItems(formattedItems);
        }
    }, [transaction]);

    const handleAddItem = (dc) => {
        setItems([...items, { id: Date.now(), dc: dc, ledger_id: '', amount: '' }]);
    };

    const handleRemoveItem = (index) => {
        const newItems = [...items];
        newItems.splice(index, 1);
        setItems(newItems);
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        setItems(newItems);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate
        const drTotal = items.filter(i => i.dc === 'D').reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
        const crTotal = items.filter(i => i.dc === 'C').reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
        
        if (Math.abs(drTotal - crTotal) > 0.01) {
            Swal.fire('Error', `Journal is unbalanced. Debits: ${drTotal.toFixed(2)}, Credits: ${crTotal.toFixed(2)}`, 'error');
            return;
        }

        if (items.some(i => !i.ledger_id || !i.amount)) {
            Swal.fire('Error', 'Please fill all ledger and amount fields', 'error');
            return;
        }

        try {
            setLoading(true);
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const token = userInfo?.token;
            await axios.put(`${API_BASE_URL}/api/entries/${transaction.id}`, {
                date,
                narration,
                entrytype_id: entrytypeId,
                items: items.map(i => ({
                    dc: i.dc,
                    ledger_id: i.ledger_id,
                    amount: i.amount
                }))
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            Swal.fire('Success', 'Journal entry updated successfully', 'success');
            onSuccess();
            onHide();
        } catch (error) {
            console.error('Error updating entry:', error);
            Swal.fire('Error', error.response?.data?.message || 'Failed to update entry', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Offcanvas show={show} onHide={onHide} placement="end" style={{ width: '800px' }}>
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title className="text-primary">Edit Journal Entry</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <form onSubmit={handleSubmit}>
                    <div className="row mb-3">
                        <div className="col-md-4">
                            <label className="form-label">Date</label>
                            <input 
                                type="date" 
                                className="form-control" 
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                required 
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Entry Type</label>
                            <select 
                                className="form-select" 
                                value={entrytypeId}
                                onChange={(e) => setEntrytypeId(e.target.value)}
                            >
                                <option value="">Select Type...</option>
                                {entrytypes.map(type => (
                                    <option key={type.id} value={type.id}>{type.label || type.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-12 mt-3">
                            <label className="form-label">Narration</label>
                            <input 
                                type="text" 
                                className="form-control" 
                                value={narration}
                                onChange={(e) => setNarration(e.target.value)}
                                required 
                            />
                        </div>
                    </div>

                    <h6 className="mt-4 mb-3 border-bottom pb-2">Entry Items</h6>
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th style={{ width: '15%' }}>Dr/Cr</th>
                                <th style={{ width: '55%' }}>Ledger Account</th>
                                <th style={{ width: '20%' }}>Amount</th>
                                <th style={{ width: '10%' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, index) => (
                                <tr key={index}>
                                    <td>
                                        <select 
                                            className="form-select form-select-sm" 
                                            value={item.dc} 
                                            onChange={(e) => handleItemChange(index, 'dc', e.target.value)}
                                        >
                                            <option value="D">Dr</option>
                                            <option value="C">Cr</option>
                                        </select>
                                    </td>
                                    <td>
                                        <Select
                                            options={ledgers}
                                            value={ledgers.flatMap(g => g.options).find(l => String(l.value) === String(item.ledger_id))}
                                            onChange={(selected) => handleItemChange(index, 'ledger_id', selected?.value)}
                                            placeholder="Search ledger..."
                                            styles={{ control: (base) => ({ ...base, minHeight: '31px', height: '31px' }) }}
                                        />
                                    </td>
                                    <td>
                                        <input 
                                            type="number" 
                                            step="0.01" 
                                            className="form-control form-control-sm text-end" 
                                            value={item.amount}
                                            onChange={(e) => handleItemChange(index, 'amount', e.target.value)}
                                            required 
                                        />
                                    </td>
                                    <td className="text-center">
                                        <button 
                                            type="button" 
                                            className="btn btn-sm btn-outline-danger border-0" 
                                            onClick={() => handleRemoveItem(index)}
                                        >
                                            <i className="fas fa-times"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan="4">
                                    <button type="button" className="btn btn-sm btn-outline-primary me-2" onClick={() => handleAddItem('D')}>+ Add Debit</button>
                                    <button type="button" className="btn btn-sm btn-outline-success" onClick={() => handleAddItem('C')}>+ Add Credit</button>
                                </td>
                            </tr>
                            <tr className="bg-light">
                                <td colSpan="2" className="text-end fw-bold">Totals:</td>
                                <td className="text-end fw-bold">
                                    <div className="text-danger">Dr: {items.filter(i => i.dc === 'D').reduce((s, i) => s + parseFloat(i.amount || 0), 0).toFixed(2)}</div>
                                    <div className="text-success">Cr: {items.filter(i => i.dc === 'C').reduce((s, i) => s + parseFloat(i.amount || 0), 0).toFixed(2)}</div>
                                </td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>

                    <div className="text-end mt-4">
                        <Button variant="secondary" className="me-2" onClick={onHide}>Cancel</Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? 'Saving...' : 'Save Entry'}
                        </Button>
                    </div>
                </form>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default EntryEditModal;
